import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A small rich-text editor for the composer.
 *
 * `contentEditable` plus `document.execCommand`. execCommand is deprecated and
 * every browser still implements it, because the replacement (writing selection
 * and range handling by hand) is a large amount of subtle code and the
 * alternative is pulling in an editor framework several times the size of this
 * entire app. For bold / italic / lists / links it is the right trade.
 *
 * The markup produced here is sanitized again before it is sent -- see
 * Composer.tsx. Anything pasted in comes from outside and is not trusted just
 * because it arrived via the user.
 */

interface Props {
	html: string;
	onChange: (html: string) => void;
	placeholder?: string;
}

interface ToolButton {
	label: string;
	title: string;
	command: string;
	value?: string;
	style?: 'bold' | 'italic' | 'underline';
}

const BUTTONS: ToolButton[] = [
	{ label: 'B', title: 'Bold', command: 'bold', style: 'bold' },
	{ label: 'I', title: 'Italic', command: 'italic', style: 'italic' },
	{ label: 'U', title: 'Underline', command: 'underline', style: 'underline' },
	{ label: '• List', title: 'Bulleted list', command: 'insertUnorderedList' },
	{ label: '1. List', title: 'Numbered list', command: 'insertOrderedList' },
	{ label: '❝', title: 'Quote', command: 'formatBlock', value: 'blockquote' }
];

export function RichText({ html, onChange, placeholder }: Props) {
	const ref = useRef<HTMLDivElement>(null);
	const [empty, setEmpty] = useState(!html);

	// Only seed the DOM when the incoming value genuinely differs. Writing
	// innerHTML on every render would move the caret to the start on each
	// keystroke, which makes the editor unusable.
	useEffect(() => {
		const node = ref.current;
		if (node && node.innerHTML !== html) {
			node.innerHTML = html;
			setEmpty(!node.textContent?.trim());
		}
	}, [html]);

	const emit = useCallback(() => {
		const node = ref.current;
		if (!node) {
			return;
		}
		setEmpty(!node.textContent?.trim());
		onChange(node.innerHTML);
	}, [onChange]);

	const exec = useCallback(
		(button: ToolButton) => {
			// Keep focus in the editable region: a toolbar click would otherwise
			// blur it and execCommand would have no selection to act on.
			ref.current?.focus();
			document.execCommand(button.command, false, button.value);
			emit();
		},
		[emit]
	);

	const addLink = useCallback(() => {
		ref.current?.focus();
		const url = window.prompt('Link address');
		if (!url) {
			return;
		}
		// Only ever create http(s) links. Without this check a pasted
		// "javascript:..." would become a clickable script in the draft, and then
		// in the recipient's mailbox.
		if (!/^https?:\/\//i.test(url)) {
			window.alert('Links must start with http:// or https://');
			return;
		}
		document.execCommand('createLink', false, url);
		emit();
	}, [emit]);

	return (
		<div className="richtext">
			<div className="richtext__tools" role="toolbar" aria-label="Formatting">
				{BUTTONS.map(button => (
					<button
						key={button.command + (button.value ?? '')}
						type="button"
						className="richtext__tool"
						title={button.title}
						aria-label={button.title}
						style={
							'bold' === button.style
								? { fontWeight: 700 }
								: 'italic' === button.style
									? { fontStyle: 'italic' }
									: 'underline' === button.style
										? { textDecoration: 'underline' }
										: undefined
						}
						// onMouseDown, not onClick: the default mousedown would blur the
						// editor and clear the selection before the command ran.
						onMouseDown={event => {
							event.preventDefault();
							exec(button);
						}}
					>
						{button.label}
					</button>
				))}
				<button
					type="button"
					className="richtext__tool"
					title="Insert link"
					aria-label="Insert link"
					onMouseDown={event => {
						event.preventDefault();
						addLink();
					}}
				>
					🔗
				</button>
				<button
					type="button"
					className="richtext__tool"
					title="Remove formatting"
					aria-label="Remove formatting"
					onMouseDown={event => {
						event.preventDefault();
						exec({ label: '', title: '', command: 'removeFormat' });
					}}
				>
					⌫
				</button>
			</div>

			<div className="richtext__surface">
				{empty && placeholder && <span className="richtext__placeholder">{placeholder}</span>}
				<div
					ref={ref}
					className="richtext__input"
					contentEditable
					suppressContentEditableWarning
					role="textbox"
					aria-multiline="true"
					aria-label="Message body"
					onInput={emit}
					onBlur={emit}
					onPaste={event => {
						// Paste as plain text. Pasted HTML drags in the source page's
						// styles, fonts and colours, which is how a tidy message turns
						// into a mess of someone else's CSS -- and it is a needless way
						// to import markup we would only have to strip again.
						event.preventDefault();
						const text = event.clipboardData.getData('text/plain');
						document.execCommand('insertText', false, text);
						emit();
					}}
				/>
			</div>
		</div>
	);
}

/**
 * Derives the plain-text alternative from the composed markup.
 *
 * Every message goes out multipart with both. Sending HTML alone is a strong
 * spam signal and is unreadable in clients that prefer text.
 */
export function htmlToPlain(html: string): string {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	doc.querySelectorAll('script, style').forEach(node => node.remove());

	// Turn block-level structure into line breaks before flattening, otherwise
	// every paragraph runs into the next one.
	doc.querySelectorAll('br').forEach(node => node.replaceWith('\n'));
	doc.querySelectorAll('p, div, li, tr, blockquote').forEach(node => node.append('\n'));
	doc.querySelectorAll('li').forEach(node => node.prepend('- '));

	return (doc.body.textContent ?? '')
		.replace(/ /g, ' ')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

/** Escapes text for embedding in composed HTML, preserving its line breaks. */
export function plainToHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.split('\n')
		.map(line => `<div>${line || '<br>'}</div>`)
		.join('');
}
